/**
 * URL Parser Utility
 *
 * Extracts and validates agent session ID from URL query parameters.
 */

/**
 * UUID v4 validation regex
 * Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 * where x is any hexadecimal digit and y is one of 8, 9, A, or B
 */
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Extract agent session ID from URL query parameter
 *
 * URL format: https://agent.btpay.com?id=<agentSessionId>
 *
 * @param url - Optional URL to parse (defaults to window.location.href)
 * @returns Agent session ID if valid, null otherwise
 *
 * @example
 * extractSessionIdFromUrl() // Uses window.location.href
 * extractSessionIdFromUrl('https://agent.btpay.com?id=123e4567-e89b-12d3-a456-426614174000')
 * // Returns: '123e4567-e89b-12d3-a456-426614174000'
 */
export function extractSessionIdFromUrl(url?: string): string | null {
  try {
    // Use provided URL or browser's current URL
    const targetUrl =
      url || (typeof window !== 'undefined' ? window.location.href : '');

    if (!targetUrl) {
      return null;
    }

    // Parse URL and extract 'id' or 'sessionId' query parameter ('id' takes priority)
    const urlObj = new URL(targetUrl);
    const agentSessionId =
      urlObj.searchParams.get('id') ?? urlObj.searchParams.get('sessionId');

    if (!agentSessionId) {
      return null;
    }

    // In development with mock data, allow non-UUID session IDs
    const isDevMode = import.meta.env.VITE_USE_MOCK_DATA === 'true';

    // Validate UUID format (skip in development mode for mock IDs)
    if (!isDevMode && !isValidUUID(agentSessionId)) {
      return null;
    }

    return agentSessionId;
  } catch {
    // Invalid URL format
    return null;
  }
}

/**
 * Validate if string is a valid UUID v4
 *
 * @param value - String to validate
 * @returns true if valid UUID v4, false otherwise
 *
 * @example
 * isValidUUID('123e4567-e89b-12d3-a456-426614174000') // true
 * isValidUUID('invalid-uuid') // false
 */
export function isValidUUID(value: string): boolean {
  return UUID_REGEX.test(value);
}

/**
 * Error types for session ID extraction
 */
export type SessionIdError = 'MISSING' | 'INVALID_FORMAT' | 'INVALID_URL';

/**
 * Extract session ID with detailed error information
 *
 * @param url - Optional URL to parse
 * @returns Object with sessionId or error type
 *
 * @example
 * const result = extractSessionIdWithError();
 * if (result.error) {
 *   console.error('Session ID error:', result.error);
 * } else {
 *   console.log('Session ID:', result.sessionId);
 * }
 */
export function extractSessionIdWithError(
  url?: string
): { sessionId: string } | { error: SessionIdError } {
  try {
    const targetUrl =
      url || (typeof window !== 'undefined' ? window.location.href : '');

    if (!targetUrl) {
      return { error: 'INVALID_URL' };
    }

    const urlObj = new URL(targetUrl);
    const agentSessionId =
      urlObj.searchParams.get('id') ?? urlObj.searchParams.get('sessionId');

    if (!agentSessionId) {
      return { error: 'MISSING' };
    }

    // In development with mock data, allow non-UUID session IDs
    const isDevMode = import.meta.env.VITE_USE_MOCK_DATA === 'true';

    if (!isDevMode && !isValidUUID(agentSessionId)) {
      return { error: 'INVALID_FORMAT' };
    }

    return { sessionId: agentSessionId };
  } catch {
    return { error: 'INVALID_URL' };
  }
}
